/**
 * A webinar's public address, when the request reached the app instead of the
 * page. In production the app worker serves the designed page itself before
 * the app is ever loaded, so landing here means there is no page at that
 * address. In local development the worker is not in front, so the page is
 * asked for from the API and shown when it exists.
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router";
import type { TroubleKind } from "../../shared/trouble";
import { TroublePage } from "./TroublePage";

/** What the page's own 404 means: a talk not open yet, or one nobody knows. */
export function troubleForPage(status: number, body: { reason?: string } | null, online = true): TroubleKind {
  if (!online) return "off";
  if (status === 404) return body?.reason === "unpublished" ? "soon" : "old";
  return "ours";
}

export function PublicPageView() {
  const { slug = "" } = useParams();
  const [trouble, setTrouble] = useState<TroubleKind | null>(null);
  useEffect(() => {
    let live = true;
    const url = `/api/w/${encodeURIComponent(slug)}/page`;
    fetch(url)
      .then(async (res) => {
        if (!live) return;
        // A deleted webinar's address went on to the main webinar.
        if (res.redirected) {
          window.location.replace(res.url);
          return;
        }
        if (res.ok) {
          // Why: the page's own scripts read the address it was opened on (an invite, a link's tags).
          window.location.replace(url + window.location.search);
          return;
        }
        const body = (await res.json().catch(() => null)) as { reason?: string } | null;
        // A quick live has no page: its address is the link it is joined from.
        if (body?.reason === "quick_live") {
          window.location.replace(`/live/${encodeURIComponent(slug)}${window.location.search}`);
          return;
        }
        setTrouble(troubleForPage(res.status, body, navigator.onLine));
      })
      .catch(() => live && setTrouble(navigator.onLine === false ? "off" : "ours"));
    return () => {
      live = false;
    };
  }, [slug]);
  if (!trouble) return null;
  return <TroublePage kind={trouble} />;
}
